<template>
  <div class="flex flex-col gap-6 max-h-[calc(100vh-22rem)] overflow-y-auto">
    <div class="p-3 bg-fill-brand-secondary rounded-xl">
      <p class="text-white/80 text-sm">
        {{ $t("channel.leaderboard.prizeForm.currentlyEditing") }}
        {{
          themeStore.currentTheme?.kind === "community"
            ? $t("adminLeaderboard.communityTheme")
            : $t("adminLeaderboard.channelTheme")
        }}
        {{ selectedCommunityDescription }}
      </p>
    </div>
    <form.Field name="title">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.prizeForm.prizeTitle')"
          required
          :placeholder="$t('channel.leaderboard.prizeForm.enterPrizeTitle')"
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>

    <!-- Points Sections -->
    <div
      v-for="(prize, index) in formValues.leaderboard_prize_points"
      :key="index"
      class="p-3 bg-fill-brand-secondary rounded-xl flex-col justify-end items-start gap-2 inline-flex"
    >
      <div class="self-stretch justify-between items-center inline-flex">
        <div class="text-white text-base font-semibold">
          {{ $t("channel.leaderboard.prizeForm.points") }}
        </div>
        <button @click="removePrize(index)">
          <Icon
            icon="material-symbols:delete-outline"
            class="text-white/40 size-6 hover:text-white transition-colors"
          />
        </button>
      </div>

      <div
        class="self-stretch h-[220px] flex-col justify-center items-start gap-2 flex"
      >
        <form.Field :name="`leaderboard_prize_points[${index}].from_position`">
          <template #default="{ field }">
            <TextInput
              :model-value="field.state.value"
              :label="$t('channel.leaderboard.prizeForm.fromPosition')"
              type="number"
              :placeholder="
                $t('channel.leaderboard.prizeForm.enterStartPosition')
              "
              :error="field.state.meta.errors[0]?.message"
              disable-number-controls
              @update:model-value="
                (value) => field.handleChange(parseInt(String(value)) || 0)
              "
            />
          </template>
        </form.Field>

        <form.Field :name="`leaderboard_prize_points[${index}].to_position`">
          <template #default="{ field }">
            <TextInput
              :model-value="field.state.value"
              :label="$t('channel.leaderboard.prizeForm.toPosition')"
              type="number"
              :placeholder="
                $t('channel.leaderboard.prizeForm.enterEndPosition')
              "
              :error="field.state.meta.errors[0]?.message"
              disable-number-controls
              @update:model-value="
                (value) => field.handleChange(parseInt(String(value)) || 0)
              "
            />
          </template>
        </form.Field>

        <form.Field :name="`leaderboard_prize_points[${index}].points`">
          <template #default="{ field }">
            <TextInput
              :model-value="field.state.value"
              :label="$t('channel.leaderboard.prizeForm.points')"
              type="number"
              :placeholder="
                $t('channel.leaderboard.prizeForm.enterPointsAmount')
              "
              :error="field.state.meta.errors[0]?.message"
              disable-number-controls
              @update:model-value="
                (value) => field.handleChange(parseInt(String(value)) || 0)
              "
            />
          </template>
        </form.Field>
      </div>
    </div>

    <!-- Add Prize Button -->
    <button
      class="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
      @click="addPrize"
    >
      <Icon icon="material-symbols:add" class="size-5" />
      <span>{{ $t("channel.leaderboard.prizeForm.addPrizeRow") }}</span>
    </button>

    <form.Field name="leaderboard_prize_points">
      <template #default="{ field: arrayField }">
        <span
          v-if="arrayField.state.meta.errors[0]?.message"
          class="text-xs text-basics-accent-red"
        >
          {{ arrayField.state.meta.errors[0]?.message }}
        </span>
      </template>
    </form.Field>
  </div>

  <div class="flex gap-4 mt-6">
    <!-- Save Button -->
    <button
      class="flex-1 h-12 bg-fill-brand rounded-full text-white font-medium hover:opacity-90 transition-opacity"
      @click="onSave()"
    >
      {{ $t("channel.leaderboard.prizeForm.save") }}
    </button>

    <!-- Issue Points Button -->
    <button
      class="flex-1 h-12 bg-fill-brand-secondary rounded-full text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      :disabled="hasUnsavedChanges"
      @click="onIssuePoints"
    >
      {{ $t("channel.leaderboard.prizeForm.issuePoints") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import TextInput from "@/components/ui/form/text-input.vue";
import { useForm } from "@tanstack/vue-form";
import { z } from "zod/v4";
import type { UpdateLeaderboardPrizeInput } from "@/generated/graphql";
import { useMutation } from "@vue/apollo-composable";
import { useToastStore } from "@/stores/toast.store";
import { useChannelStore } from "@/stores/channel.store";
import { graphql as gql } from "@/generated";
import { useThemeStore } from "@/stores/theme.store";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const IssueLeaderboardPrizePointsDocument = gql(/* GraphQL */ `
  mutation IssueLeaderboardPrizePoints(
    $input: IssueLeaderboardPrizePointsInput!
  ) {
    issueLeaderboardPrizePoints(input: $input) {
      ... on IssueLeaderboardPrizePointsSuccess {
        success
      }
    }
  }
`);

const props = defineProps<{
  prize: UpdateLeaderboardPrizeInput;
}>();

const emit = defineEmits<{
  save: [UpdateLeaderboardPrizeInput];
}>();

const toastStore = useToastStore();
const channelStore = useChannelStore();
const themeStore = useThemeStore();

const selectedCommunityDescription = computed(
  () =>
    channelStore.channelCommunities.find(
      (cc) => cc.id === channelStore.selectedCommunity,
    )?.description,
);

const validatePrizes = (
  prizes: { from_position: number; to_position: number; points: number }[],
) => {
  const sortedPrizes = [...prizes].sort(
    (a, b) => a.from_position - b.from_position,
  );

  for (let i = 0; i < sortedPrizes.length; i++) {
    const currentPrize = sortedPrizes[i];
    if (!currentPrize) continue;

    if (currentPrize.to_position < currentPrize.from_position) {
      return false;
    }

    if (i < sortedPrizes.length - 1) {
      const nextPrize = sortedPrizes[i + 1];
      if (!nextPrize) continue;

      if (currentPrize.to_position >= nextPrize.from_position) {
        return false;
      }
    }
  }

  return true;
};

const schema = z.object({
  title: z.string().min(1, t("channel.leaderboard.prizeForm.errors.title")),
  leaderboard_prize_points: z
    .array(
      z.object({
        from_position: z
          .number()
          .min(1, t("channel.leaderboard.prizeForm.errors.positionMin"))
          .int(t("channel.leaderboard.prizeForm.errors.positionInt")),
        to_position: z
          .number()
          .min(1, t("channel.leaderboard.prizeForm.errors.positionMin"))
          .int(t("channel.leaderboard.prizeForm.errors.positionInt")),
        points: z
          .number()
          .min(1, t("channel.leaderboard.prizeForm.errors.pointsMin"))
          .int(t("channel.leaderboard.prizeForm.errors.pointsInt"))
          .max(1000000, t("channel.leaderboard.prizeForm.errors.pointsMax")),
      }),
    )
    .min(1, t("channel.leaderboard.prizeForm.errors.atLeastOnePrize"))
    .refine(validatePrizes, {
      error: t("channel.leaderboard.prizeForm.errors.prizePositionsOverlap"),
    }),
});

const form = useForm({
  defaultValues: {
    title: props.prize.title,
    leaderboard_prize_points: props.prize.leaderboard_prize_points?.length
      ? props.prize.leaderboard_prize_points.map((point) => ({
          from_position: point.from_position,
          to_position: point.to_position,
          points: point.points,
        }))
      : [
          {
            from_position: 1,
            to_position: 1,
            points: 0,
          },
        ],
  },
  validators: {
    onChange: schema,
  },
  onSubmit: async ({ value }) => {
    const sortedPrizes = [...value.leaderboard_prize_points].sort(
      (a, b) => a.from_position - b.from_position,
    );

    const payload: UpdateLeaderboardPrizeInput = {
      id: props.prize.id,
      title: value.title,
      type: props.prize.type,
      leaderboard_prize_points: sortedPrizes.map((prize) => ({
        points: prize.points,
        from_position: prize.from_position,
        to_position: prize.to_position,
      })),
    };

    emit("save", payload);
  },
});

const formValues = form.useStore((state) => state.values);

const hasUnsavedChanges = computed(() => {
  if (!formValues.value.title || !formValues.value.leaderboard_prize_points)
    return false;

  if (formValues.value.title !== props.prize.title) return true;

  if (
    formValues.value.leaderboard_prize_points.length !==
    props.prize.leaderboard_prize_points?.length
  )
    return true;

  return formValues.value.leaderboard_prize_points.some((prize, index) => {
    const originalPrize = props.prize.leaderboard_prize_points?.[index];
    if (!originalPrize) return true;

    return (
      prize.from_position !== originalPrize.from_position ||
      prize.to_position !== originalPrize.to_position ||
      prize.points !== originalPrize.points
    );
  });
});

const addPrize = () => {
  const current = formValues.value.leaderboard_prize_points;
  if (!current) return;
  const lastPrize = current[current.length - 1];
  const nextPosition = lastPrize ? lastPrize.to_position + 1 : 1;

  form.setFieldValue("leaderboard_prize_points", [
    ...current,
    {
      from_position: nextPosition,
      to_position: nextPosition,
      points: 2000,
    },
  ]);
};

const removePrize = (index: number) => {
  const current = formValues.value.leaderboard_prize_points;
  if (!current || current.length === 1) return;
  form.setFieldValue(
    "leaderboard_prize_points",
    current.filter((_, i) => i !== index),
  );
};

const issueLeaderboardPrizePoints = useMutation(
  IssueLeaderboardPrizePointsDocument,
);

const onIssuePoints = async () => {
  try {
    await issueLeaderboardPrizePoints.mutate({
      input: {
        id: props.prize.id,
      },
    });
    channelStore.lastLeaderboardUpdate = Date.now();
    toastStore.addToast({
      message: t("channel.leaderboard.prizeForm.pointsIssuedSuccessfully"),
      type: "success",
    });
  } catch (error) {
    console.error(error);
    toastStore.addToast({
      message: t("channel.leaderboard.prizeForm.pointsIssueFailed"),
      type: "error",
    });
  }
};

function onSave() {
  form.handleSubmit();
}

defineExpose({
  save: onSave,
});
</script>
